import React, { useEffect, useMemo, useState } from "react";
import PageWrapper from "../../components/PageWrapper/PageWrapper";
import venueApiService from "../../services/venueApi";
import venueDashboardApiService from "../../services/venueDashboardApi";
import { clearStripeFeatureOverrides } from "../../utils/stripeFeatureFlag";
import { clearVenueRecordCache } from "../../utils/venueRecordCache";

const stripeFlagFromVenue = (venue) => Boolean(venue?.stripe_payments_enabled);
const platformFeeFlagFromVenue = (venue) => venue?.platform_fee_enabled !== false;
const depositEnabledFromVenue = (venue) => Boolean(venue?.deposit_enabled);
const depositPercentFromVenue = (venue) => {
  const pct = Number(venue?.deposit_percent);
  return Number.isFinite(pct) && pct > 0 ? pct : 20;
};

const depositPercentMatches = (venue, expected) =>
  Math.abs(Number(depositPercentFromVenue(venue)) - Number(expected)) < 0.01;

const AdminVenuePayments = () => {
  const [venues, setVenues] = useState([]);
  const [draftById, setDraftById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    clearStripeFeatureOverrides();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadVenues = async () => {
      try {
        setLoading(true);
        setError("");
        setSaveMessage("");
        setSaveError("");
        const allVenues = await venueApiService.getAllVenues({ forceRefresh: true });
        if (!cancelled) {
          const sorted = [...(Array.isArray(allVenues) ? allVenues : [])].sort((a, b) =>
            String(a?.name || "").localeCompare(String(b?.name || ""))
          );
          setVenues(sorted);
          setDraftById({});
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Failed to load venues.");
          setVenues([]);
          setDraftById({});
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadVenues();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  const savedById = useMemo(() => {
    const map = {};
    venues.forEach((venue) => {
      const id = String(venue?.id || "");
      if (!id) return;
      map[id] = {
        stripe: stripeFlagFromVenue(venue),
        platformFee: platformFeeFlagFromVenue(venue),
        depositEnabled: depositEnabledFromVenue(venue),
        depositPercent: depositPercentFromVenue(venue)
      };
    });
    return map;
  }, [venues]);

  const displayById = useMemo(() => {
    const map = {};
    Object.entries(savedById).forEach(([id, saved]) => {
      const draft = draftById[id] || {};
      map[id] = {
        stripe: draft.stripe ?? saved.stripe,
        platformFee: draft.platformFee ?? saved.platformFee,
        depositEnabled: draft.depositEnabled ?? saved.depositEnabled,
        depositPercent: draft.depositPercent ?? saved.depositPercent
      };
    });
    return map;
  }, [savedById, draftById]);

  const dirtyVenueIds = useMemo(
    () =>
      Object.keys(displayById).filter((id) => {
        const saved = savedById[id];
        const display = displayById[id];
        if (!saved || !display) return false;
        return (
          display.stripe !== saved.stripe ||
          display.platformFee !== saved.platformFee ||
          display.depositEnabled !== saved.depositEnabled ||
          Number(display.depositPercent) !== Number(saved.depositPercent)
        );
      }),
    [displayById, savedById]
  );

  const hasUnsavedChanges = dirtyVenueIds.length > 0;

  const handleToggleDraft = (venueId, field, nextEnabled) => {
    const venueIdKey = String(venueId || "");
    if (!venueIdKey) return;

    setSaveMessage("");
    setSaveError("");
    setDraftById((prev) => {
      const saved = savedById[venueIdKey];
      if (!saved) return prev;

      const current = {
        stripe: prev[venueIdKey]?.stripe ?? saved.stripe,
        platformFee: prev[venueIdKey]?.platformFee ?? saved.platformFee,
        depositEnabled: prev[venueIdKey]?.depositEnabled ?? saved.depositEnabled,
        depositPercent: prev[venueIdKey]?.depositPercent ?? saved.depositPercent
      };
      const next = { ...current, [field]: Boolean(nextEnabled) };

      const unchanged =
        next.stripe === saved.stripe &&
        next.platformFee === saved.platformFee &&
        next.depositEnabled === saved.depositEnabled &&
        Number(next.depositPercent) === Number(saved.depositPercent);

      if (unchanged) {
        const copy = { ...prev };
        delete copy[venueIdKey];
        return copy;
      }

      return {
        ...prev,
        [venueIdKey]: next
      };
    });
  };

  const handleDepositPercentDraft = (venueId, rawValue) => {
    const venueIdKey = String(venueId || "");
    if (!venueIdKey) return;
    const parsed = Number(rawValue);
    const depositPercent = Number.isFinite(parsed) ? Math.min(100, Math.max(1, parsed)) : 20;
    setSaveMessage("");
    setSaveError("");
    setDraftById((prev) => {
      const saved = savedById[venueIdKey];
      if (!saved) return prev;
      const current = {
        stripe: prev[venueIdKey]?.stripe ?? saved.stripe,
        platformFee: prev[venueIdKey]?.platformFee ?? saved.platformFee,
        depositEnabled: prev[venueIdKey]?.depositEnabled ?? saved.depositEnabled,
        depositPercent: prev[venueIdKey]?.depositPercent ?? saved.depositPercent
      };
      const next = { ...current, depositPercent };
      const unchanged =
        next.stripe === saved.stripe &&
        next.platformFee === saved.platformFee &&
        next.depositEnabled === saved.depositEnabled &&
        Number(next.depositPercent) === Number(saved.depositPercent);
      if (unchanged) {
        const copy = { ...prev };
        delete copy[venueIdKey];
        return copy;
      }
      return { ...prev, [venueIdKey]: next };
    });
  };

  const handleDiscardChanges = () => {
    setDraftById({});
    setSaveMessage("");
    setSaveError("");
  };

  const handleSaveChanges = async () => {
    if (!hasUnsavedChanges || saving) return;

    setSaving(true);
    setSaveMessage("");
    setSaveError("");

    const updates = dirtyVenueIds.map((venueIdKey) => ({
      venueIdKey,
      stripe: displayById[venueIdKey].stripe,
      platformFee: displayById[venueIdKey].platformFee,
      depositEnabled: displayById[venueIdKey].depositEnabled,
      depositPercent: displayById[venueIdKey].depositPercent
    }));

    try {
      const results = await Promise.allSettled(
        updates.map(({ venueIdKey, stripe, platformFee, depositEnabled, depositPercent }) =>
          venueDashboardApiService
            .updateVenueDetails(venueIdKey, {
              stripe_payments_enabled: stripe,
              platform_fee_enabled: platformFee,
              deposit_enabled: depositEnabled,
              deposit_percent: depositEnabled ? depositPercent : null
            })
            .then((updated) => ({
              venueIdKey,
              stripe,
              platformFee,
              depositEnabled,
              depositPercent,
              updated
            }))
        )
      );

      const failed = results
        .map((result, index) => ({ result, ...updates[index] }))
        .filter(({ result }) => result.status === "rejected");

      if (failed.length > 0) {
        const failedNames = failed
          .map(({ venueIdKey }) => venues.find((v) => String(v.id) === venueIdKey)?.name || venueIdKey)
          .join(", ");
        throw new Error(`Failed to save: ${failedNames}`);
      }

      const mismatched = results
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value)
        .filter(
          ({ stripe, platformFee, depositEnabled, depositPercent, updated }) =>
            stripeFlagFromVenue(updated) !== stripe ||
            platformFeeFlagFromVenue(updated) !== platformFee ||
            depositEnabledFromVenue(updated) !== depositEnabled ||
            !depositPercentMatches(updated, depositPercent)
        );

      if (mismatched.length > 0) {
        throw new Error(
          "The server did not confirm your changes. Please try again shortly."
        );
      }

      dirtyVenueIds.forEach((venueIdKey) => clearVenueRecordCache(venueIdKey));
      clearVenueRecordCache();

      const allVenues = await venueApiService.getAllVenues({ forceRefresh: true });
      const sorted = [...(Array.isArray(allVenues) ? allVenues : [])].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""))
      );
      setVenues(sorted);
      setDraftById({});
      setSaveMessage(
        updates.length === 1
          ? "Saved 1 venue."
          : `Saved ${updates.length} venues.`
      );
    } catch (e) {
      setSaveError(e?.message || "Could not save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageWrapper useNewFooter useLanding2Header>
      <main className="bg-white">
        <div className="mx-auto w-full max-w-[96rem] px-6 py-10 sm:px-8">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-gray-500">
                Admin
              </p>
              <h1 className="font-heading mt-2 text-3xl font-bold text-gray-900">
                Venue payments
              </h1>
              <p className="mt-2 text-sm text-gray-500">
                Route: <code>/admin/venues/payments</code>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {hasUnsavedChanges ? (
                <button
                  type="button"
                  onClick={handleDiscardChanges}
                  disabled={saving}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Discard
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleSaveChanges}
                disabled={!hasUnsavedChanges || saving || loading}
                className="rounded-lg bg-[#046242] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#035238] focus:outline-none focus:ring-2 focus:ring-[#046242]/40 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save changes"}
              </button>
              <button
                type="button"
                onClick={() => setRefreshTick((prev) => prev + 1)}
                disabled={saving}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Refresh
              </button>
            </div>
          </div>

          {hasUnsavedChanges ? (
            <p className="mb-4 text-sm text-amber-700">
              You have unsaved changes for {dirtyVenueIds.length} venue
              {dirtyVenueIds.length === 1 ? "" : "s"}. Click Save changes to persist.
            </p>
          ) : null}
          {saveMessage ? <p className="mb-4 text-sm text-green-700">{saveMessage}</p> : null}
          {saveError ? <p className="mb-4 text-sm text-red-600">{saveError}</p> : null}

          <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Payment settings by venue</h2>
              <p className="mt-1 text-sm text-gray-600">
                Control Stripe payment UI, platform fee, and optional booking deposits (% of hire +
                add-ons). Card processing fees are charged on the balance payment only.
              </p>
            </div>
            {loading ? <p className="text-sm text-gray-600">Loading venues...</p> : null}
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            {!loading && !error && venues.length === 0 ? (
              <p className="text-sm text-gray-600">No venues found.</p>
            ) : null}
            {!loading && !error && venues.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Venue
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Venue ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Stripe payments UI
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Platform fee (£6)
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Deposit
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Deposit %
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Pending
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {venues.map((venue) => {
                        const venueIdKey = String(venue?.id || "");
                        const display = displayById[venueIdKey] || {
                          stripe: stripeFlagFromVenue(venue),
                          platformFee: platformFeeFlagFromVenue(venue),
                          depositEnabled: depositEnabledFromVenue(venue),
                          depositPercent: depositPercentFromVenue(venue)
                        };
                        const isDirty = dirtyVenueIds.includes(venueIdKey);
                        return (
                          <tr key={venueIdKey || venue?.name}>
                            <td className="px-4 py-3 text-sm text-gray-800">
                              <div className="font-medium text-gray-900">{venue?.name || "Untitled venue"}</div>
                              <div className="text-xs text-gray-500">{venue?.city || "—"}</div>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-600">{venueIdKey || "—"}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={display.stripe}
                                  disabled={saving || !venueIdKey}
                                  onChange={(e) =>
                                    handleToggleDraft(venueIdKey, "stripe", e.target.checked)
                                  }
                                />
                                <span>{display.stripe ? "Enabled" : "Disabled"}</span>
                              </label>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={display.platformFee}
                                  disabled={saving || !venueIdKey}
                                  onChange={(e) =>
                                    handleToggleDraft(venueIdKey, "platformFee", e.target.checked)
                                  }
                                />
                                <span>{display.platformFee ? "Charged" : "Waived"}</span>
                              </label>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={display.depositEnabled}
                                  disabled={saving || !venueIdKey || !display.stripe}
                                  onChange={(e) =>
                                    handleToggleDraft(venueIdKey, "depositEnabled", e.target.checked)
                                  }
                                />
                                <span>{display.depositEnabled ? "On" : "Off"}</span>
                              </label>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              <input
                                type="number"
                                min={1}
                                max={100}
                                step={1}
                                disabled={
                                  saving || !venueIdKey || !display.depositEnabled || !display.stripe
                                }
                                value={display.depositPercent}
                                onChange={(e) =>
                                  handleDepositPercentDraft(venueIdKey, e.target.value)
                                }
                                className="w-16 rounded border border-gray-300 px-2 py-1 text-sm"
                              />
                            </td>
                            <td className="px-4 py-3 text-xs">
                              {isDirty ? (
                                <span className="font-medium text-amber-700">Unsaved</span>
                              ) : (
                                <span className="text-gray-500">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </main>
    </PageWrapper>
  );
};

export default AdminVenuePayments;
